import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { type AnswerOption, type Question } from '~/hooks/useSurveyQuery'
import { findByPath, getByPath, pathOr } from '~/utils'

import { sortFunctionAtom, tabulationResultAtom } from '../atoms'
import { countColumnKeyToId, type CountData } from '../utils'

import { useMapCountColumnKey } from './useMapCountColumnKey'
import { useMapDiffColumnKey } from './useMapDiffColumnKey'
import { useTabulatedQuestions } from './useTabulatedQuestions'

type AnswerData = CountData & {
  question: Question
  answerOption: AnswerOption
}

export const useAnswersArrayData = (): AnswerData[][] => {
  const questions = useTabulatedQuestions()
  const tabulationResult = useAtomValue(tabulationResultAtom)
  const mapCountColumnKey = useMapCountColumnKey()
  const mapDiffColumnKey = useMapDiffColumnKey()
  const sort = useAtomValue(sortFunctionAtom)

  return React.useMemo(
    () =>
      questions.map((question) =>
        R.pipe(
          question.answer_options,
          R.map((answerOption) => {
            const results = mapCountColumnKey((countColumnKey) =>
              R.pipe(
                tabulationResult ?? [],
                findByPath(
                  'byCustomerAttributeId',
                  countColumnKeyToId(countColumnKey),
                ),
                getByPath('result'),
              ),
            )

            const counts = mapCountColumnKey((countColumnKey) =>
              R.pipe(
                results[countColumnKey],
                pathOr('questions', []),
                findByPath('questionId', question.question_id),
                pathOr('result.0.answers', []),
                findByPath('answerOption.option', answerOption.option),
                pathOr('count', NaN),
              ),
            )
            const diffCounts = mapDiffColumnKey(
              (_diffColumnKey, [left, right]) => counts[left] - counts[right],
            )

            const ratios = mapCountColumnKey((countColumnKey) => {
              const count = counts[countColumnKey]
              const totalCount = results[countColumnKey]?.totalCount ?? NaN

              return count / totalCount
            })
            const diffRatios = mapDiffColumnKey(
              (_diffColumnKey, [left, right]) => ratios[left] - ratios[right],
            )

            return {
              question,
              answerOption,
              counts: {
                ...counts,
                ...diffCounts,
              },
              ratios: {
                ...ratios,
                ...diffRatios,
              },
            }
          }),
          sort,
        ),
      ),
    [mapCountColumnKey, mapDiffColumnKey, questions, sort, tabulationResult],
  )
}
